import { scoreQueryOptions } from "../scores/scoreQueryOptions";
import { playsRefetchIntervalMs } from "./gameDetailLogic";
/**
 * The game detail Box score tab: an away and home segmented control, then that team's
 * tables, one per stat category (ISSUE-990). Refreshes at the detail's
 * cadence while live and slowly while idle, through the query scheduler. A missing box score is a race
 * with the tab offer, not an incident, so it shows a quiet line instead of
 * an error modal.
 */
import { useEventPlayerBoxScoreGet } from "@sportsmet/events-sdk/private";
import { SegmentedControl, Skeleton } from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import { BoxScoreTable } from "./BoxScoreTable";
import { GameSectionCard, GameSectionEmpty } from "./GameSectionCard";
import {
  BOX_SCORE_TEAM_EMPTY,
  BOX_SCORE_TEAM_SWITCHER_LABEL,
  BOX_SCORE_UNAVAILABLE,
  type typeBoxScoreSide,
  type typeBoxScoreTeam,
  type typeBoxScoreTeamInfo,
} from "./boxScoreView";
import { resolvePlatePair } from "./teamPlateText";

export const BoxScoreTab = (props: {
  eventId: string;
  isLive: boolean;
  refreshAfterSeconds: number | null;
  away: typeBoxScoreTeamInfo;
  home: typeBoxScoreTeamInfo;
}): ReactNode => {
  const { eventId, isLive, refreshAfterSeconds, away, home } = props;
  const [side, setSide] = useState<typeBoxScoreSide>("away");
  const query = useEventPlayerBoxScoreGet(
    { eventId },
    {
      ...scoreQueryOptions,
      refetchInterval: () =>
        playsRefetchIntervalMs({ isLive, refreshAfterSeconds }),
    },
  );

  if (query.isPending) {
    return (
      <div data-testid="box-score-tab-loading" className="flex flex-col gap-4">
        <Skeleton shape="card" />
        <Skeleton shape="card" />
      </div>
    );
  }

  const data = query.data?.data ?? null;
  if (data === null) {
    return (
      <GameSectionCard>
        <GameSectionEmpty
          testId="box-score-unavailable"
          line={BOX_SCORE_UNAVAILABLE}
        />
      </GameSectionCard>
    );
  }

  const { awayText, homeText } = resolvePlatePair({ away, home });
  const selectedTeam: typeBoxScoreTeam =
    side === "home" ? data.home : data.away;
  const selectedInfo = side === "home" ? home : away;

  return (
    <div data-testid="box-score-tab" className="flex flex-col gap-4">
      {/* The team switch is a segmented control, a secondary switch inside
          the tab, never a second tab row (spec 7.2). */}
      <SegmentedControl
        label={BOX_SCORE_TEAM_SWITCHER_LABEL}
        value={side}
        fullWidth
        testId="box-score-team-switch"
        options={[
          { value: "away", label: awayText },
          { value: "home", label: homeText },
        ]}
        onValueChange={(next) => {
          setSide(next === "home" ? "home" : "away");
        }}
      />
      {selectedTeam.tables.length === 0 ? (
        <GameSectionCard>
          <GameSectionEmpty
            testId="box-score-team-empty"
            line={BOX_SCORE_TEAM_EMPTY}
          />
        </GameSectionCard>
      ) : (
        selectedTeam.tables.map((table) => (
          <BoxScoreTable key={table.key} table={table} team={selectedInfo} />
        ))
      )}
    </div>
  );
};
